import {
  useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import { useKeycloak } from '../../authentication/keycloak/KeycloakProvider';
import type { EditableFloorPlan } from '../models/editor-feature';
import { getFloorPlansForOffice } from '../services/floorplan-editor.service';
import { normalizeFloorPlan, type StoredFloorPlan } from '../utils/normalize-floorplan';
import { withOnlyDeskShapes } from '../utils/desk-shape-outliers';

interface FloorPlanEditorState {
  readonly loading: boolean;
  readonly error: boolean;
  /** Every storey this office has a floorplan for, ascending. */
  readonly storeys: number[];
  readonly floorPlan?: EditableFloorPlan;
  /** The selected storey's plan as the server sent it, for laying a draft over. */
  readonly stored?: StoredFloorPlan;
  /**
   * Re-reads the office's floorplans.
   *
   * Needed after a save: the server normalises what it stores and creates workplaces for new
   * desks, so the copy held here stops being what the database actually has the moment a save
   * succeeds.
   */
  readonly refresh: () => void;
}

interface Props {
  readonly officeId: string | undefined;
  readonly storey: number | undefined;
}

/**
 * Loads an office's floorplans and hands back one storey, normalised for editing.
 *
 * The whole set is fetched because that is the only endpoint there is — floorplans are
 * returned per office, not per storey — and it is also what populates the storey switcher.
 */
export const useFloorPlanEditor = ({ officeId, storey }: Props): FloorPlanEditorState => {
  const keycloak = useKeycloak();
  const [storedFloorPlans, setStoredFloorPlans] = useState<StoredFloorPlan[] | undefined>();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [reloadToken, setReloadToken] = useState(0);

  useEffect(() => {
    if (!officeId) return undefined;

    let active = true;
    setLoading(true);
    setError(false);

    getFloorPlansForOffice(officeId, keycloak)
      .then((floorPlans) => {
        if (!active) return;

        setStoredFloorPlans(floorPlans);
        setError(floorPlans === undefined);
      })
      .catch(() => {
        if (active) setError(true);
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
    };
  }, [officeId, keycloak, reloadToken]);

  const refresh = useCallback(() => setReloadToken((token) => token + 1), []);

  // The plan last shown, by id.
  const lastOpen = useRef<string | undefined>();

  const storeys = useMemo(
    () =>
      (storedFloorPlans ?? [])
        .map((floorPlan) => floorPlan.storey ?? 0)
        .sort((first, second) => first - second),
    [storedFloorPlans],
  );

  const stored = useMemo(() => {
    if (!storedFloorPlans?.length) return undefined;

    // With no storey chosen yet, open the lowest one rather than whichever the API happened
    // to return first — an office whose plans come back in insertion order would otherwise
    // open on an arbitrary floor.
    const lowest = [...storedFloorPlans].sort(
      (first, second) => (first.storey ?? 0) - (second.storey ?? 0),
    )[0];

    // A storey not in the list yet is one just given to the open plan, or to a new one, before
    // the list is read again: keep the plan that is open until it is, rather than jumping to
    // the lowest floor and back.
    return storedFloorPlans.find((candidate) => candidate.storey === storey)
      ?? storedFloorPlans.find((candidate) => lastOpen.current !== undefined && candidate.id === lastOpen.current)
      ?? lowest;
  }, [storedFloorPlans, storey]);

  useEffect(() => {
    lastOpen.current = stored?.id;
  }, [stored]);

  const floorPlan = useMemo(() => {
    if (!stored) return undefined;

    // Stored desk marks that are not desks — anything not drawn as the "[" a desk is, and an
    // L-shaped corner of wall several times the size of every desk on the plan — are shown as
    // the floor shapes they are, unless an icon was chosen for them. The next save writes them
    // back that way; until then nothing about the stored plan changes.
    return withOnlyDeskShapes(normalizeFloorPlan(stored));
  }, [stored]);

  return {
    loading, error, storeys, floorPlan, stored, refresh,
  };
};

export default useFloorPlanEditor;
